import type { HTMLAttributes, PropsWithChildren } from 'react';
import { forwardRef, useEffect, useState } from 'react';
import { Masonry as MasonryComponent } from '@elfsight/lib-masonry-grid';
import { useRerender, useWindowWidth } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import { useRect } from 'react-use-rect';
import type { Job } from '../../../types';
import { Item } from '../items';
import { LayoutContainer } from '../LayoutContainer';
import { useLayout } from '../LayoutProvider';

type MasonryProps = HTMLAttributes<HTMLDivElement> & {
  jobs: Job[];
};

export const Masonry = forwardRef<HTMLDivElement, MasonryProps>(
  ({ jobs, ...forwardingProps }, ref) => {
    const { masonryLayoutColumns: columns, masonryLayoutGap: gap } =
      useSettings();

    const windowWidth = useWindowWidth();
    const [shouldUpdateHeight, setShouldUpdateHeight] = useState(false);
    const updateHeightHandler = () => {
      setShouldUpdateHeight((prevState) => !prevState);
    };

    const rerenderCallback = useRerender();
    const { setRerender, rerender } = useLayout();

    useEffect(() => {
      setRerender?.(rerenderCallback);
    }, [rerenderCallback, setRerender]);

    useEffect(() => {
      rerender?.();
      // oxlint-disable-next-line react-hooks/exhaustive-deps
    }, [windowWidth]);

    return (
      <LayoutContainer gap={gap} ref={ref} {...forwardingProps}>
        <MasonryComponent
          className="Masonry__Container-sc"
          columns={columns}
          basis={280}
          gap={gap}
          updateHeights={shouldUpdateHeight}
        >
          {jobs.map((item) => (
            <HeightObservableMasonryItem
              onHeightChange={updateHeightHandler}
              key={item.id}
            >
              <Item data={item} />
            </HeightObservableMasonryItem>
          ))}
        </MasonryComponent>
      </LayoutContainer>
    );
  }
);

type HeightObservableMasonryItemProps = PropsWithChildren & {
  onHeightChange?: (newHeight: number) => void;
};

export function HeightObservableMasonryItem({
  onHeightChange,
  children,
  ...forwardingProps
}: HeightObservableMasonryItemProps) {
  const [itemHeight, setItemHeight] = useState(0);
  const [ref] = useRect(
    ({ height }) => {
      if (itemHeight !== height) {
        setItemHeight(height);
        onHeightChange?.(height);
      }
    },
    { resize: true }
  );

  return (
    <div {...forwardingProps} ref={ref}>
      {children}
    </div>
  );
}
